<template>
	<div>
		<div class="page-loading" v-if="!ready">
			<div class="loading">
				<div class="logo-image">
					<img :src="`/logo-wide.png`" style="max-width: 150px" alt="App Logo" />
				</div>
				<v-progress-circular :size="50" color="primary" indeterminate />
			</div>
		</div>

		<div v-else class="sg-landing">
			<!-- Hero -->
			<section class="sg-hero">
				<div class="sg-hero-shimmer"></div>
				<v-container class="relative" style="position: relative; z-index: 1;">
					<v-row align="center">
						<v-col cols="12" md="6" class="sg-fade-in">
							<h1 class="sg-hero-title mb-6">
								<span>{{ businessTitle }}</span><br />
								زیبایی طلا، اصالت و اعتماد
							</h1>
							<p class="sg-hero-desc mb-8">
								خرید آنلاین طلا و جواهر با ضمانت اصالت، فاکتور معتبر، امکان خرید قسطی و ساخت سفارشی.
								مجموعه‌ای از بهترین طراحی‌های طلا ۱۸ عیار.
							</p>
							<div class="d-flex flex-wrap" style="gap: 12px;">
								<v-btn class="sg-btn-gold" large @click="scrollTo('categories')">
									<v-icon left>mdi-diamond-stone</v-icon>
									مشاهده محصولات
								</v-btn>
								<v-btn class="sg-btn-outline-gold" large outlined @click="scrollTo('about')">
									درباره ما
								</v-btn>
							</div>
						</v-col>
						<v-col cols="12" md="6" class="text-center d-none d-md-flex justify-center">
							<div class="sg-hero-jewel">
								<v-icon size="160" color="#D4AF37" class="sg-hero-jewel-icon">mdi-diamond-stone</v-icon>
								<div class="sg-hero-jewel-glow"></div>
							</div>
						</v-col>
					</v-row>
				</v-container>
			</section>

			<!-- Gold Price Bar -->
			<GoldPriceBar :editable="editMode" :data="contents.find(x => x.key === siteContentKeys.SafeGoldsGoldPrice)" :contentKey="siteContentKeys.SafeGoldsGoldPrice" @update="(price) => goldPrice = price" />

			<!-- Categories & Products -->
			<CategoriesSection @order="openOrderModal" :goldPrice="goldPrice" />

			<!-- Latest Jewelry Slider -->
			<LatestSection :editable="editMode" :data="contents.find(x => x.key === siteContentKeys.SafeGoldsLatest)" :goldPrice="goldPrice" :contentKey="siteContentKeys.SafeGoldsLatest" @order="openOrderModal" />

			<!-- Benefits -->
			<BenefitsSection />

			<!-- Testimonials -->
			<TestimonialsSection :editable="editMode" :data="contents.find(x => x.key === siteContentKeys.SafeGoldsTestimonials)" :contentKey="siteContentKeys.SafeGoldsTestimonials" />

			<!-- About -->
			<AboutSection :editable="editMode" :data="contents.find(x => x.key === siteContentKeys.SafeGoldsAbout)" :contentKey="siteContentKeys.SafeGoldsAbout" />

			<!-- Secure Buying -->
			<SecureBuyingSection />
		</div>

		<!-- NewOrder Modal -->
		<v-dialog v-model="orderModal" max-width="700px" scrollable persistent>
			<v-card>
				<v-card-title class="d-flex align-center">
					<v-icon color="#C9A227" class="ml-2">mdi-cart-plus</v-icon>
					ثبت سفارش
					<v-spacer />
					<v-btn icon @click="orderModal = false"><v-icon>mdi-close</v-icon></v-btn>
				</v-card-title>
				<v-divider />
				<v-card-text class="pa-4">
					<new-order ref="newOrder" :customerCode="Number(ccode)" />
				</v-card-text>
			</v-card>
		</v-dialog>
	</div>
</template>

<script>
	import { get } from 'vuex-pathify'
	import api from '@/util/app-api'
	import toast from '@/util/toast'
	import { siteContentKeys } from '@/util/enums'
	import './styles.css'

	export default {
		name: 'SafeGoldsPlansView',
		components: {
			GoldPriceBar: () => import('./Parts/GoldPriceBar.vue'),
			CategoriesSection: () => import('./Parts/CategoriesSection.vue'),
			LatestSection: () => import('./Parts/LatestSection.vue'),
			BenefitsSection: () => import('./Parts/BenefitsSection.vue'),
			TestimonialsSection: () => import('./Parts/TestimonialsSection.vue'),
			AboutSection: () => import('./Parts/AboutSection.vue'),
			SecureBuyingSection: () => import('./Parts/SecureBuyingSection.vue'),
			'new-order': () => import('@/views/customerLanding/NewOrder.vue'),
		},
		data: () => ({
			siteContentKeys,
			ccode: 0,
			goldPrice: 0,
			contents: [],
			ready: false,
			orderModal: false,
			selectedProduct: null,
		}),
		computed: {
			...get('siteConfig', ['customerCode', 'fullTitle']),
			...get('appSettings', ['landingSettings']),
			...get('app', ['editMode']),
			businessTitle() {
				return this.landingSettings?.businessTitle || this.fullTitle || 'طلای سیف زاده'
			},
		},
		mounted() {
			this.ccode = this.customerCode || this.$route.params.customerCode || 0
			this.loadContents()
		},
		methods: {
			loadContents() {
				this.ready = false
				const data = {
					keys: [
						siteContentKeys.SafeGoldsGoldPrice,
						siteContentKeys.SafeGoldsAbout,
						siteContentKeys.SafeGoldsTestimonials,
						siteContentKeys.SafeGoldsLatest,
					],
				}
				return api.post('SiteContent/ListbyKeys/' + this.ccode, data, { authHeader: false, contentType: 'application/json' })
					.then(response => {
						this.contents = response
					})
					.catch(err => {
						toast.error(err.message)
					})
					.finally(() => {
						this.ready = true
					})
			},
			openOrderModal(product) {
				this.selectedProduct = product
				this.orderModal = true
				this.$nextTick(() => {
					const orderRef = this.$refs.newOrder
					if (orderRef) {
						orderRef.orderSubmitted = false
						orderRef.message = ''
						const desc = product.weight
							? `وزن: ${product.weight} گرم — اجرت: ${product.wage}٪ — قیمت: ${Number(product.price).toLocaleString('fa-IR')} تومان`
							: ''
						orderRef.localData = {
							productName: product.name,
							description: desc,
						}
					}
				})
			},
			scrollTo(id) {
				const el = document.getElementById(id)
				if (el) {
					const offset = 80
					const top = el.getBoundingClientRect().top + window.scrollY - offset
					window.scrollTo({ top, behavior: 'smooth' })
				}
			},
		},
	}
</script>

<style lang="scss"
	scoped>
		.sg-hero-jewel {
			position: relative;
			width: 280px;
			height: 280px;
			display: flex;
			align-items: center;
			justify-content: center;
		}

		.sg-hero-jewel-icon {
			position: relative;
			z-index: 2;
			filter: drop-shadow(0 8px 32px rgba(201, 162, 39, 0.4));
			animation: sgJewelSpin 20s linear infinite;
		}

		.sg-hero-jewel-glow {
			position: absolute;
			width: 200px;
			height: 200px;
			border-radius: 50%;
			background: radial-gradient(circle, rgba(201, 162, 39, 0.25) 0%, transparent 70%);
			animation: sgGlowPulse 3s ease-in-out infinite;
		}

		@keyframes sgJewelSpin {
			0% {
				transform: rotate(0deg);
			}

			100% {
				transform: rotate(360deg);
			}
		}

		@keyframes sgGlowPulse {

			0%,
			100% {
				transform: scale(1);
				opacity: 0.6;
			}

			50% {
				transform: scale(1.2);
				opacity: 1;
			}
		}

	</style>
